
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw } from "lucide-react";
import PrintableCategoriesManager from "./PrintableCategoriesManager";
import PrintableItemsManager from "./PrintableItemsManager";

interface PrintableCategory {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface PrintableItem {
  id: string;
  category_id: string | null;
  title: string;
  description: string | null;
  zip_url: string;
  image_url: string | null;
  display_order: number;
  is_active: boolean;
  is_free: boolean;
  created_at: string;
  updated_at: string;
}

const PrintablesManager = () => {
  const [categories, setCategories] = useState<PrintableCategory[]>([]);
  const [items, setItems] = useState<PrintableItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoriesResponse, itemsResponse] = await Promise.all([
        supabase
          .from('printable_categories')
          .select('*')
          .order('display_order', { ascending: true }),
        supabase
          .from('printable_items')
          .select('*')
          .order('display_order', { ascending: true })
      ]);

      if (categoriesResponse.error) throw categoriesResponse.error;
      if (itemsResponse.error) throw itemsResponse.error;

      setCategories(categoriesResponse.data || []);
      setItems(itemsResponse.data || []);
    } catch (error: any) {
      console.error('Error fetching printables data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch printables data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">Printables Management</h2>
          <p className="text-slate-600">Manage Premium Coloring & Printables content</p>
        </div>
        <Button onClick={fetchData} disabled={loading} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{categories.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {categories.filter(c => c.is_active).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{items.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {items.filter(i => i.is_active).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Management Tabs */}
      <Tabs defaultValue="categories" className="space-y-6">
        <TabsList>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="items">Items</TabsTrigger>
        </TabsList>

        <TabsContent value="categories">
          <PrintableCategoriesManager />
        </TabsContent>

        <TabsContent value="items">
          <PrintableItemsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PrintablesManager;
